import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";

import Button from "components/Button";
import Form from "../Form";
import FormErrors from "../FormErrors";
import GoogleSignIn from "../GoogleSignIn";

import { useUnauthenticatedRoutes } from "utils/routes";

import styles from "./style.module.scss";

type Props = {
  invite: {
    email: string;
    organizationName: string;
    invitedByFullname: string;
  };
  googleAuthorizePath: string;
  googleClientId: string | null | undefined;
  enterpriseSamlSignInUrl: string | null | undefined;
};

const Login: React.FunctionComponent<Props> = ({
  invite,
  googleClientId,
  googleAuthorizePath,
  enterpriseSamlSignInUrl,
}) => {
  const { userSignUp, userSignIn, userPasswordReset } =
    useUnauthenticatedRoutes();
  const [googleErrors, setGoogleErrors] = useState<string[]>([]);
  const [enterpriseSsoOverride, setEnterpriseSsoOverride] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus the email input after component mounts, but only if we're showing the login form
    if (
      emailInputRef.current &&
      (!enterpriseSamlSignInUrl || enterpriseSsoOverride)
    ) {
      emailInputRef.current.focus();
    }
  }, [enterpriseSamlSignInUrl, enterpriseSsoOverride]);

  if (enterpriseSamlSignInUrl && !enterpriseSsoOverride) {
    return (
      <>
        <div className="header-notice">
          <Button
            bare
            onClick={(evt) => {
              evt.preventDefault();
              setEnterpriseSsoOverride(true);
            }}
          >
            Sign In with Fallback Authentication
          </Button>
        </div>
        <div className="flex-container">
          <div className="form-container">
            <h1>Sign In</h1>
            <p>Your organization has configured Single Sign-On:</p>
            <a
              href={enterpriseSamlSignInUrl}
              className={classNames("btn", "btn-success", styles.ssoLink)}
            >
              Continue with Single Sign-On
            </a>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="header-notice">
        Not signed up yet? <a href={userSignUp()}>Sign up now.</a>
      </div>
      <div className="flex-container">
        <div className="form-container">
          <Form id="new_user" action={userSignIn()} method="post" insideSection>
            <h1>Sign In</h1>
            {!invite && <p>Access your existing pganalyze account:</p>}
            {invite && (
              <p>
                You're invited to the organization "{invite.organizationName}"
                by {invite.invitedByFullname}.
                <br />
                <br />
                Please sign in to accept the invitation.
              </p>
            )}
            <input
              ref={emailInputRef}
              className="form-control"
              type="email"
              name="user[email]"
              id="user_email"
              required
              placeholder="Email"
            />
            <input
              className="form-control"
              type="password"
              name="user[password]"
              id="user_password"
              placeholder="Password"
            />
            <div className="checkbox">
              <label>
                <input name="user[remember_me]" type="hidden" value="0" />
                <input
                  type="checkbox"
                  value="1"
                  defaultChecked
                  name="user[remember_me]"
                  id="user_remember_me"
                />
                Remember me
              </label>
            </div>
            <input
              type="submit"
              name="commit"
              value="Sign in"
              className="btn btn-success"
              data-disable-with="Sign in"
            />
          </Form>
          <a href={userPasswordReset()} className={styles.forgotPassword}>
            Forgot your password?
          </a>
          {(googleClientId || enterpriseSamlSignInUrl) && (
            <div className={styles.altSignIn}>
              <div className={styles.divider}>
                <span className="px-3 text-[13px] text-[#595959]">or</span>
              </div>
              {googleClientId && (
                <>
                  <GoogleSignIn
                    authorizePath={googleAuthorizePath}
                    googleClientId={googleClientId}
                    errorHandler={setGoogleErrors}
                  />
                  <FormErrors errors={googleErrors} />
                </>
              )}
              {enterpriseSamlSignInUrl && (
                <a href={enterpriseSamlSignInUrl} className={styles.altButton}>
                  Sign In with SSO
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Login;
